import React, { useState, useEffect } from 'react';
import api from '../utils/axiosConfig';
import './Reports.css';
import './TableStyles.css';

const Reports = () => {
  const [activeView, setActiveView] = useState('managerial'); // managerial, truckDetails, filters
  const [reportData, setReportData] = useState({
    totalTrucks: 0,
    averageWaitTime: '0 minutos',
    averageUnloadTime: '0 minutos',
    averageStayTime: '0 minutos',
    trucksByPartner: [],
    dockUsage: [],
    checkerPerformance: [],
    trucksByDate: [],
    trucksByMonth: [],
    trucksByTipo: [] // Add trucksByTipo array
  });
  
  const [truckDetails, setTruckDetails] = useState([]);
  const [truckPlate, setTruckPlate] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    partner: '',
    status: '',
    plate: '',
    checker: '',
    dock: '',
    tipo: '' // Add tipo filter
  });
  
  const [partners, setPartners] = useState([]);
  const [checkers, setCheckers] = useState([]);
  const [docks, setDocks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partnersRes, checkersRes, docksRes] = await Promise.all([
          api.get('/partners'),
          api.get('/checkers'),
          api.get('/docks')
        ]);
        
        setPartners(partnersRes.data);
        setCheckers(checkersRes.data);
        setDocks(docksRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const generateManagerialReport = async () => {
    setLoading(true);
    try {
      const res = await api.post('/reports/managerial', filters);
      setReportData(res.data);
      setActiveView('managerial-results'); // Switch to the results view
      
      // Show a message if no trucks were found
      if (res.data.totalTrucks === 0) {
        alert('Nenhum caminhão encontrado com os filtros aplicados.');
      }
    } catch (error) {
      console.error('Error generating managerial report:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      alert('Erro ao gerar relatório: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const searchTruckDetails = async () => {
    if (!truckPlate.trim()) {
      alert('Por favor, informe a placa do caminhão.');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.get(`/reports/truck/${truckPlate}`);
      setTruckDetails(res.data);
    } catch (error) {
      console.error('Error getting truck details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      alert('Erro ao buscar detalhes do caminhão: ' + errorMessage);
      setTruckDetails([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to export truck details to Excel
  const exportTruckDetailsToExcel = () => {
    if (truckDetails.length === 0) return;
    
    // Create CSV content
    let csvContent = 'Registro,Data/Hora Chegada,Placa Carreta,Placa Cavalo,Motorista,CPF,Parceiro,Status,Início Descarga,Fim Descarga,Checkout,Doca,Conferente,Volumes,Tipo\n';
    
    truckDetails.forEach((truck, index) => {
      const row = [
        index + 1,
        truck.entryTimeFormatted,
        truck.trailerPlate,
        truck.truckPlate || 'N/A',
        truck.driverName || 'N/A',
        truck.driverCpf || 'N/A',
        truck.partnerName,
        truck.status,
        truck.startTimeFormatted,
        truck.endTimeFormatted,
        truck.checkoutTimeFormatted,
        truck.dockName,
        truck.checkerName,
        truck.volume || 'N/A',
        truck.tipo || 'DESCARREGAMENTO'
      ].map(field => {
        // Escape commas and wrap in quotes if needed
        if (typeof field === 'string' && field.includes(',')) {
          return `"${field}"`;
        }
        return field;
      }).join(',');
      
      csvContent += row + '\n';
    });
    
    // Create a Blob with the CSV content
    const blob = new Blob(['\ufeff', csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `detalhes_placa_${truckPlate}_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    
    // Append to the document, click and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToCSV = async () => {
    try {
      const response = await api.post('/reports/export/csv', filters, {
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      alert('Erro ao exportar para CSV: ' + errorMessage);
    }
  };

  const exportToPDF = async () => {
    try {
      const response = await api.post('/reports/export/pdf', filters, {
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      alert('Erro ao exportar para PDF: ' + errorMessage);
    }
  };

  return (
    <div className="reports-container">
      <h2>Relatórios</h2>
      
      <div className="reports-navigation">
        <button 
          className={`nav-button ${activeView === 'managerial' ? 'active' : ''}`}
          onClick={() => setActiveView('managerial')}
        >
          Relatório Gerencial
        </button>
        <button 
          className={`nav-button ${activeView === 'truckDetails' ? 'active' : ''}`}
          onClick={() => setActiveView('truckDetails')}
        >
          Detalhes por Placa
        </button>
        <button 
          className={`nav-button ${activeView === 'filters' ? 'active' : ''}`}
          onClick={() => setActiveView('filters')}
        >
          Filtros Avançados
        </button>
      </div>
      
      {activeView === 'managerial' ? (
        <div className="managerial-report-view">
          <div className="filters-section">
            <h3>Filtros</h3>
            <div className="filters-grid">
              <div className="filter-group">
                <label htmlFor="startDate">Data Inicial</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="endDate">Data Final</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="partner">Parceiro</label>
                <select
                  id="partner"
                  name="partner"
                  value={filters.partner}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos</option>
                  {partners.map(partner => (
                    <option key={partner._id} value={partner._id}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos</option>
                  <option value="AGUARDANDO">Aguardando</option>
                  <option value="EM_DESCARGA">Em Descarga</option>
                  <option value="CONCLUIDO">Concluído</option>
                  <option value="CHECKOUT">Checkout</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="tipo">Tipo</label>
                <select
                  id="tipo"
                  name="tipo"
                  value={filters.tipo}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos</option>
                  <option value="DESCARREGAMENTO">Descarregamento</option>
                  <option value="GIRO">Giro</option>
                  <option value="CAPTAÇÃO">Captação</option>
                  <option value="AGREGADO">Agregado</option>
                </select>
              </div>
            </div>
            
            <div className="filter-actions">
              <button className="btn-generate" onClick={generateManagerialReport} disabled={loading}>
                {loading ? 'Gerando...' : 'Gerar Relatório'}
              </button>
            </div>
          </div>
          
          <div className="report-results">
            <div className="report-header">
              <h3>Resultados Gerenciais</h3>
              <div className="export-buttons">
                <button className="btn-export" onClick={exportToCSV}>
                  Exportar Excel
                </button>
                <button className="btn-export" onClick={exportToPDF}>
                  Exportar PDF
                </button>
              </div>
            </div>
            
            <div className="metrics-grid">
              <div className="metric-card">
                <h4>Total de Caminhões</h4>
                <p className="metric-value">{reportData.totalTrucks}</p>
              </div>
              
              <div className="metric-card">
                <h4>Tempo Médio de Espera</h4>
                <p className="metric-value">{reportData.averageWaitTime}</p>
              </div>
              
              <div className="metric-card">
                <h4>Tempo Médio de Descarga</h4>
                <p className="metric-value">{reportData.averageUnloadTime}</p>
              </div>
              
              <div className="metric-card">
                <h4>Tempo Médio de Permanência</h4>
                <p className="metric-value">{reportData.averageStayTime}</p>
              </div>
            </div>
            
            <div className="report-charts">
              <div className="chart-section">
                <h4>Caminhões por Parceiro</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Parceiro</th>
                        <th>Quantidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.trucksByPartner.map((item, index) => (
                        <tr key={index}>
                          <td>{item.partner}</td>
                          <td>{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="chart-section">
                <h4>Utilização de Docas</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Doca</th>
                        <th>Tempo Ativo</th>
                        <th>Quantidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.dockUsage.map((item, index) => (
                        <tr key={index}>
                          <td>{item.dock}</td>
                          <td>{item.activeTime}</td>
                          <td>{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="chart-section">
                <h4>Desempenho de Conferentes</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Conferente</th>
                        <th>Quantidade</th>
                        <th>Tempo Médio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.checkerPerformance.map((item, index) => (
                        <tr key={index}>
                          <td>{item.checker}</td>
                          <td>{item.trucks}</td>
                          <td>{item.averageTime}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="chart-section">
                <h4>Volumetria por Dia</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Quantidade</th>
                        <th>Volumes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.trucksByDate.map((item, index) => (
                        <tr key={index}>
                          <td>{item.date}</td>
                          <td>{item.count}</td>
                          <td>{item.volume}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="chart-section">
                <h4>Volumetria por Mês</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Mês</th>
                        <th>Quantidade</th>
                        <th>Volumes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.trucksByMonth.map((item, index) => (
                        <tr key={index}>
                          <td>{item.month}</td>
                          <td>{item.count}</td>
                          <td>{item.volume}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="chart-section">
                <h4>Caminhões por Tipo</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Quantidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.trucksByTipo && reportData.trucksByTipo.map((item, index) => (
                        <tr key={index}>
                          <td>{item.tipo}</td>
                          <td>{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : activeView === 'truckDetails' ? (
        <div className="truck-details-view">
          <h3>Detalhes por Placa</h3>
          <div className="truck-search">
            <div className="filter-group">
              <label htmlFor="truckPlate">Placa do Caminhão</label>
              <input
                type="text"
                id="truckPlate"
                value={truckPlate}
                onChange={(e) => setTruckPlate(e.target.value.toUpperCase())}
                placeholder="ABC1D23"
              />
            </div>
            <button className="btn-generate" onClick={searchTruckDetails} disabled={loading}>
              {loading ? 'Buscando...' : 'Buscar Detalhes'}
            </button>
          </div>
          
          {truckDetails.length > 0 && (
            <div className="truck-details-results">
              <div className="results-header">
                <h4>Resultados Encontrados: {truckDetails.length} registro(s)</h4>
                <div className="export-buttons">
                  <button className="btn-export" onClick={exportTruckDetailsToExcel}>
                    Exportar para Excel
                  </button>
                </div>
              </div>
              
              {/* Table View */}
              <div className="table-container">
                <table className="details-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Data/Hora Chegada</th>
                      <th>Placa Carreta</th>
                      <th>Placa Cavalo</th>
                      <th>Motorista</th>
                      <th>CPF</th>
                      <th>Parceiro</th>
                      <th>Status</th>
                      <th>Início Descarga</th>
                      <th>Fim Descarga</th>
                      <th>Checkout</th>
                      <th>Doca</th>
                      <th>Conferente</th>
                      <th>Volumes</th>
                      <th>Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {truckDetails.map((truck, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{truck.entryTimeFormatted}</td>
                        <td>{truck.trailerPlate}</td>
                        <td>{truck.truckPlate || 'N/A'}</td>
                        <td>{truck.driverName || 'N/A'}</td>
                        <td>{truck.driverCpf || 'N/A'}</td>
                        <td>{truck.partnerName}</td>
                        <td>{truck.status}</td>
                        <td>{truck.startTimeFormatted}</td>
                        <td>{truck.endTimeFormatted}</td>
                        <td>{truck.checkoutTimeFormatted}</td>
                        <td>{truck.dockName}</td>
                        <td>{truck.checkerName}</td>
                        <td>{truck.volume || 'N/A'}</td>
                        <td>{truck.tipo || 'DESCARREGAMENTO'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : activeView === 'filters' ? (
        <div className="filters-section">
          <h3>Filtros Avançados</h3>
          <div className="filters-grid">
            <div className="filter-group">
              <label htmlFor="startDate">Data Inicial</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="endDate">Data Final</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="partner">Parceiro</label>
              <select
                id="partner"
                name="partner"
                value={filters.partner}
                onChange={handleFilterChange}
              >
                <option value="">Todos</option>
                {partners.map(partner => (
                  <option key={partner._id} value={partner._id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">Todos</option>
                <option value="AGUARDANDO">Aguardando</option>
                <option value="EM_DESCARGA">Em Descarga</option>
                <option value="CONCLUIDO">Concluído</option>
                <option value="CHECKOUT">Checkout</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="plate">Placa</label>
              <input
                type="text"
                id="plate"
                name="plate"
                value={filters.plate}
                onChange={handleFilterChange}
                placeholder="ABC1D23"
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="checker">Conferente</label>
              <select
                id="checker"
                name="checker"
                value={filters.checker}
                onChange={handleFilterChange}
              >
                <option value="">Todos</option>
                {checkers.map(checker => (
                  <option key={checker._id} value={checker._id}>
                    {checker.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="dock">Doca</label>
              <select
                id="dock"
                name="dock"
                value={filters.dock}
                onChange={handleFilterChange}
              >
                <option value="">Todas</option>
                {docks.map(dock => (
                  <option key={dock._id} value={dock._id}>
                    Doca {dock.number}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="filter-actions">
            <button className="btn-generate" onClick={generateManagerialReport} disabled={loading}>
              {loading ? 'Gerando...' : 'Gerar Relatório'}
            </button>
          </div>
        </div>
      ) : activeView === 'managerial-results' ? (
        <div className="managerial-report-view">
          <div className="filters-section">
            <h3>Filtros Aplicados</h3>
            <div className="filters-grid">
              <div className="filter-group">
                <label htmlFor="startDate">Data Inicial</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  disabled
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="endDate">Data Final</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  disabled
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="partner">Parceiro</label>
                <select
                  id="partner"
                  name="partner"
                  value={filters.partner}
                  onChange={handleFilterChange}
                  disabled
                >
                  <option value="">Todos</option>
                  {partners.map(partner => (
                    <option key={partner._id} value={partner._id}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  disabled
                >
                  <option value="">Todos</option>
                  <option value="AGUARDANDO">Aguardando</option>
                  <option value="EM_DESCARGA">Em Descarga</option>
                  <option value="CONCLUIDO">Concluído</option>
                  <option value="CHECKOUT">Checkout</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="plate">Placa</label>
                <input
                  type="text"
                  id="plate"
                  name="plate"
                  value={filters.plate}
                  onChange={handleFilterChange}
                  placeholder="ABC1D23"
                  disabled
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="checker">Conferente</label>
                <select
                  id="checker"
                  name="checker"
                  value={filters.checker}
                  onChange={handleFilterChange}
                  disabled
                >
                  <option value="">Todos</option>
                  {checkers.map(checker => (
                    <option key={checker._id} value={checker._id}>
                      {checker.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="dock">Doca</label>
                <select
                  id="dock"
                  name="dock"
                  value={filters.dock}
                  onChange={handleFilterChange}
                  disabled
                >
                  <option value="">Todas</option>
                  {docks.map(dock => (
                    <option key={dock._id} value={dock._id}>
                      Doca {dock.number}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="tipo">Tipo</label>
                <select
                  id="tipo"
                  name="tipo"
                  value={filters.tipo}
                  onChange={handleFilterChange}
                  disabled
                >
                  <option value="">Todos</option>
                  <option value="DESCARREGAMENTO">Descarregamento</option>
                  <option value="GIRO">Giro</option>
                  <option value="CAPTAÇÃO">Captação</option>
                  <option value="AGREGADO">Agregado</option>
                </select>
              </div>
            </div>
            
            <div className="filter-actions">
              <button className="btn-generate" onClick={() => setActiveView('filters')}>
                Novos Filtros
              </button>
            </div>
          </div>
          
          <div className="report-results">
            <div className="report-header">
              <h3>Resultados Gerenciais</h3>
              <div className="export-buttons">
                <button className="btn-export" onClick={exportToCSV}>
                  Exportar Excel
                </button>
                <button className="btn-export" onClick={exportToPDF}>
                  Exportar PDF
                </button>
              </div>
            </div>
            
            <div className="metrics-grid">
              <div className="metric-card">
                <h4>Total de Caminhões</h4>
                <p className="metric-value">{reportData.totalTrucks}</p>
              </div>
              
              <div className="metric-card">
                <h4>Tempo Médio de Espera</h4>
                <p className="metric-value">{reportData.averageWaitTime}</p>
              </div>
              
              <div className="metric-card">
                <h4>Tempo Médio de Descarga</h4>
                <p className="metric-value">{reportData.averageUnloadTime}</p>
              </div>
              
              <div className="metric-card">
                <h4>Tempo Médio de Permanência</h4>
                <p className="metric-value">{reportData.averageStayTime}</p>
              </div>
            </div>
            
            <div className="report-charts">
              <div className="chart-section">
                <h4>Caminhões por Parceiro</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Parceiro</th>
                        <th>Quantidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.trucksByPartner.map((item, index) => (
                        <tr key={index}>
                          <td>{item.partner}</td>
                          <td>{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="chart-section">
                <h4>Utilização de Docas</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Doca</th>
                        <th>Tempo Ativo</th>
                        <th>Quantidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.dockUsage.map((item, index) => (
                        <tr key={index}>
                          <td>{item.dock}</td>
                          <td>{item.activeTime}</td>
                          <td>{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="chart-section">
                <h4>Desempenho de Conferentes</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Conferente</th>
                        <th>Quantidade</th>
                        <th>Tempo Médio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.checkerPerformance.map((item, index) => (
                        <tr key={index}>
                          <td>{item.checker}</td>
                          <td>{item.trucks}</td>
                          <td>{item.averageTime}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="chart-section">
                <h4>Volumetria por Dia</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Quantidade</th>
                        <th>Volumes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.trucksByDate.map((item, index) => (
                        <tr key={index}>
                          <td>{item.date}</td>
                          <td>{item.count}</td>
                          <td>{item.volume}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="chart-section">
                <h4>Volumetria por Mês</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Mês</th>
                        <th>Quantidade</th>
                        <th>Volumes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.trucksByMonth.map((item, index) => (
                        <tr key={index}>
                          <td>{item.month}</td>
                          <td>{item.count}</td>
                          <td>{item.volume}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="chart-section">
                <h4>Caminhões por Tipo</h4>
                <div className="global-table-container">
                  <table className="global-table">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Quantidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.trucksByTipo && reportData.trucksByTipo.map((item, index) => (
                        <tr key={index}>
                          <td>{item.tipo}</td>
                          <td>{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="managerial-report-view">
          <div className="filters-section">
            <h3>Filtros</h3>
            <div className="filters-grid">
              <div className="filter-group">
                <label htmlFor="startDate">Data Inicial</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="endDate">Data Final</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="partner">Parceiro</label>
                <select
                  id="partner"
                  name="partner"
                  value={filters.partner}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos</option>
                  {partners.map(partner => (
                    <option key={partner._id} value={partner._id}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos</option>
                  <option value="AGUARDANDO">Aguardando</option>
                  <option value="EM_DESCARGA">Em Descarga</option>
                  <option value="CONCLUIDO">Concluído</option>
                  <option value="CHECKOUT">Checkout</option>
                </select>
              </div>
            </div>
            
            <div className="filter-actions">
              <button className="btn-generate" onClick={generateManagerialReport} disabled={loading}>
                {loading ? 'Gerando...' : 'Gerar Relatório'}
              </button>
            </div>
          </div>
          
          <div className="report-results">
            <div className="report-header">
              <h3>Resultados Gerenciais</h3>
              <div className="export-buttons">
                <button className="btn-export" onClick={exportToCSV}>
                  Exportar Excel
                </button>
                <button className="btn-export" onClick={exportToPDF}>
                  Exportar PDF
                </button>
              </div>
            </div>
            
            <div className="metrics-grid">
              <div className="metric-card">
                <h4>Total de Caminhões</h4>
                <p className="metric-value">{reportData.totalTrucks}</p>
              </div>
              
              <div className="metric-card">
                <h4>Tempo Médio de Espera</h4>
                <p className="metric-value">{reportData.averageWaitTime}</p>
              </div>
              
              <div className="metric-card">
                <h4>Tempo Médio de Descarga</h4>
                <p className="metric-value">{reportData.averageUnloadTime}</p>
              </div>
              
              <div className="metric-card">
                <h4>Tempo Médio de Permanência</h4>
                <p className="metric-value">{reportData.averageStayTime}</p>
              </div>
            </div>
            
            <div className="report-charts">
              <div className="chart-section">
                <h4>Caminhões por Parceiro</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Parceiro</th>
                      <th>Quantidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.trucksByPartner.map((item, index) => (
                      <tr key={index}>
                        <td>{item.partner}</td>
                        <td>{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="chart-section">
                <h4>Utilização de Docas</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Doca</th>
                      <th>Tempo Ativo</th>
                      <th>Quantidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.dockUsage.map((item, index) => (
                      <tr key={index}>
                        <td>{item.dock}</td>
                        <td>{item.activeTime}</td>
                        <td>{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="chart-section">
                <h4>Desempenho de Conferentes</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Conferente</th>
                      <th>Quantidade</th>
                      <th>Tempo Médio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.checkerPerformance.map((item, index) => (
                      <tr key={index}>
                        <td>{item.checker}</td>
                        <td>{item.trucks}</td>
                        <td>{item.averageTime}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="chart-section">
                <h4>Volumetria por Dia</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Quantidade</th>
                      <th>Volumes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.trucksByDate.map((item, index) => (
                      <tr key={index}>
                        <td>{item.date}</td>
                        <td>{item.count}</td>
                        <td>{item.volume}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="chart-section">
                <h4>Volumetria por Mês</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Mês</th>
                      <th>Quantidade</th>
                      <th>Volumes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.trucksByMonth.map((item, index) => (
                      <tr key={index}>
                        <td>{item.month}</td>
                        <td>{item.count}</td>
                        <td>{item.volume}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="chart-section">
                <h4>Caminhões por Tipo</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Quantidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.trucksByTipo && reportData.trucksByTipo.map((item, index) => (
                      <tr key={index}>
                        <td>{item.tipo}</td>
                        <td>{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;